import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { servicesData } from '../data/servicesData';
import ServiceModal from './ServiceModal';
import ScheduleModal from './ScheduleModal';

const ServicesSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [selectedService, setSelectedService] = useState(null);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  const handleLearnMore = (service) => {
    setSelectedService(service);
    setIsServiceModalOpen(true);
  };

  const handleSchedule = () => {
    setIsScheduleModalOpen(true);
  };

  return (
    <section id="services" ref={ref} className="section-padding bg-[#0a0e27] relative overflow-hidden" data-testid="services-section">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-[#00d4ff]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-[#0099ff]/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-block glass px-6 py-2 rounded-full mb-6">
            <span className="text-[#00d4ff] font-semibold uppercase tracking-wider text-sm">
              {t('services.badge')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('services.title')}
            <span className="gradient-text block mt-2">{t('services.subtitle')}</span>
          </h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto">
            {t('services.description')}
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {servicesData.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="group relative"
            >
              <div className="glass-strong rounded-3xl overflow-hidden h-full">
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={t(service.titleKey)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e27] to-transparent" />
                  
                  {/* Icon */}
                  <div 
                    className="absolute top-4 right-4 w-14 h-14 rounded-2xl flex items-center justify-center glass-strong"
                    style={{ backgroundColor: `${service.color}20` }}
                  >
                    <service.icon className="text-2xl" style={{ color: service.color }} />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#00d4ff] transition-colors">
                    {t(service.titleKey)}
                  </h3>
                  <p className="text-white/70 mb-4 leading-relaxed">
                    {t(service.descKey)}
                  </p>

                  {/* Features */}
                  <div className="space-y-2 mb-6">
                    {service.featuresKeys.map((featureKey, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div 
                          className="w-1.5 h-1.5 rounded-full" 
                          style={{ backgroundColor: service.color }}
                        />
                        <span className="text-white/60 text-sm">{t(featureKey)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Button */}
                  <button 
                    onClick={() => handleLearnMore(service)}
                    className="text-[#00d4ff] hover:text-white font-semibold flex items-center space-x-2 group/btn"
                  >
                    <span>Learn More</span>
                    <svg 
                      className="w-5 h-5 group-hover/btn:translate-x-2 transition-transform" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
          className="text-center mt-16"
        >
          <div className="glass-strong rounded-3xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-[#00d4ff]/10 to-[#0099ff]/10" />
            <div className="relative z-10">
              <h3 className="text-3xl font-bold text-white mb-4">
                {t('services.cta')}
              </h3>
              <p className="text-white/70 mb-6 max-w-2xl mx-auto">
                {t('services.ctaDesc')}
              </p>
              <button onClick={handleSchedule} className="btn-primary">{t('services.ctaButton')}</button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Modals */}
      <ServiceModal
        isOpen={isServiceModalOpen}
        onClose={() => setIsServiceModalOpen(false)}
        service={selectedService}
      />
      <ScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
      />
    </section>
  );
};

export default ServicesSection;
